import React, { useEffect, useMemo, useState } from 'react';
import { X, Phone, Copy, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { ServiceExecutiveService } from '../../services/serviceExecutiveService';

const STATUS_OPTIONS = ['pending', 'assigned', 'in_progress', 'completed', 'cancelled'];

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  assigned: 'bg-purple-100 text-purple-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-emerald-100 text-emerald-700',
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const openMapLink = (value) => {
  if (!value) return;
  const url = value.startsWith('http')
    ? value
    : `https://www.google.com/maps/search/${encodeURIComponent(value)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const copyText = async (value) => {
  try {
    await navigator.clipboard.writeText(value);
    alert('Copied to clipboard');
  } catch (error) {
    console.error('Copy failed:', error);
    alert('Failed to copy');
  }
};

const ServiceRequestDetailModal = ({ open, requestId, onClose, user }) => {
  const [loading, setLoading] = useState(false);
  const [request, setRequest] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusRemark, setStatusRemark] = useState('');

  const loadData = async () => {
    if (!requestId) return;
    setLoading(true);
    const [requestRes, historyRes] = await Promise.all([
      ServiceExecutiveService.getServiceRequestById(requestId),
      ServiceExecutiveService.getServiceRequestStatusHistory(requestId),
    ]);
    setRequest(requestRes.data || null);
    setStatusHistory(historyRes.data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, requestId]);

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      alert('Please select a status');
      return;
    }
    const result = await ServiceExecutiveService.updateServiceRequestStatus(
      requestId,
      newStatus,
      user?.id,
      statusRemark || `Status updated to ${newStatus}`
    );
    if (result.success) {
      alert(result.message || 'Status updated');
      setStatusModalOpen(false);
      setNewStatus('');
      setStatusRemark('');
      loadData();
    } else {
      alert(result.message || 'Failed to update status');
    }
  };

  const statusHistoryItems = useMemo(
    () =>
      statusHistory.map((item) => ({
        id: item.id || `${item.status}-${item.changed_at}`,
        status: item.status,
        changedAt: formatDateTime(item.changed_at),
        remarks: item.remarks,
        actor: item.actor_name || 'System',
      })),
    [statusHistory]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Service Request Details</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center">
            <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full spinner mx-auto mb-3"></div>
            <p className="text-gray-600">Loading details...</p>
          </div>
        ) : !request ? (
          <div className="p-10 text-center">
            <p className="text-gray-600">Service request not found.</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">{request.customer_name}</h4>
                <p className="text-sm text-gray-600">{request.vehicle_number} · {request.service_type}</p>
              </div>
              <div className="flex items-center gap-2">
                {request.priority && (
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${PRIORITY_COLORS[request.priority] || 'bg-gray-100 text-gray-600'}`}>
                    {request.priority.toUpperCase()}
                  </span>
                )}
                {request.request_status && (
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_COLORS[request.request_status] || 'bg-gray-100 text-gray-600'}`}>
                    {request.request_status.replace('_', ' ').toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Contact</p>
                <div className="flex items-center gap-2 text-gray-800">
                  <span>{request.contact_number || 'N/A'}</span>
                  {request.contact_number && (
                    <>
                      <button type="button" onClick={() => copyText(request.contact_number)} className="text-indigo-600 hover:text-indigo-800">
                        <Copy size={14} />
                      </button>
                      <a href={`tel:${request.contact_number}`} className="text-blue-600 hover:text-blue-800">
                        <Phone size={14} />
                      </a>
                    </>
                  )}
                </div>
                {request.alternate_number && (
                  <div className="flex items-center gap-2 text-gray-800">
                    <span>{request.alternate_number}</span>
                    <button type="button" onClick={() => copyText(request.alternate_number)} className="text-indigo-600 hover:text-indigo-800">
                      <Copy size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Timeline</p>
                <p className="text-sm text-gray-800">Requested: {formatDateTime(request.requested_at)}</p>
                {request.expected_completion_date && (
                  <p className="text-sm text-gray-800">Expected: {formatDateTime(request.expected_completion_date)}</p>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-2">Problem Description</p>
              <p className="text-gray-800">{request.problem || 'No description provided.'}</p>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-gray-500">Address</p>
              <div className="flex items-center gap-2 text-gray-800">
                <span>{request.address || 'No address provided'}</span>
                <button type="button" onClick={() => copyText(request.address || '')} className="text-indigo-600 hover:text-indigo-800">
                  <Copy size={14} />
                </button>
              </div>
              {request.location_link && (
                <button type="button" onClick={() => openMapLink(request.location_link)} className="text-indigo-600 hover:text-indigo-800 text-sm inline-flex items-center gap-1">
                  Open location <ExternalLink size={14} />
                </button>
              )}
              {request.drop_location && (
                <button type="button" onClick={() => openMapLink(request.drop_location)} className="text-indigo-600 hover:text-indigo-800 text-sm inline-flex items-center gap-1">
                  Drop location <ExternalLink size={14} />
                </button>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-sm text-gray-500">Media</p>
              {request.media_upload && request.media_upload.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {request.media_upload.map((url, index) => (
                    <button
                      type="button"
                      key={`${url}-${index}`}
                      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                      className="w-20 h-20 border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50"
                    >
                      <img src={url} alt="Attachment" className="object-cover w-full h-full" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <ImageIcon size={16} /> No media uploaded
                </div>
              )}
            </div>

            {statusHistoryItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Status History</p>
                <div className="space-y-3">
                  {statusHistoryItems.map((item) => (
                    <div key={item.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600'}`}>
                          {item.status.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">{item.changedAt}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Updated by: {item.actor}</p>
                      {item.remarks && <p className="text-sm text-gray-700 mt-2">{item.remarks}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setStatusModalOpen(true)}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Update Status
              </button>
            </div>
          </div>
        )}
      </div>

      {statusModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900">Update Status</h4>
              <button type="button" onClick={() => setStatusModalOpen(false)} className="text-gray-500">
                <X size={18} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setNewStatus(option)}
                  className={`px-3 py-1 rounded-full border text-sm ${
                    newStatus === option ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-200'
                  }`}
                >
                  {option.replace('_', ' ').toUpperCase()}
                </button>
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
              <textarea
                value={statusRemark}
                onChange={(event) => setStatusRemark(event.target.value)}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
                rows={3}
                placeholder="Add remarks about this status change..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setStatusModalOpen(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700">
                Cancel
              </button>
              <button type="button" onClick={handleUpdateStatus} className="px-4 py-2 rounded-lg bg-indigo-600 text-white">
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestDetailModal;
